import { create } from 'zustand';
import { notificationService } from '../services/notificationService';

/**
 * Notifications store — powers the bell dropdown in the Navbar.
 *
 * Keeps two pieces of state: the full notifications array (loaded once
 * when the dropdown opens) and an `unreadCount` that gets refreshed
 * periodically by the Navbar. Polling the count is cheap; polling the
 * full list would be overkill.
 */
export const useNotificationsStore = create((set, get) => ({
  notifications: [],
  unreadCount: 0,
  loading: false,
  loaded: false,
  filters: {
    platform: 'all',
  },

  fetch: async () => {
    set({ loading: true });
    try {
      const { platform } = get().filters;
      const notifications = await notificationService.list({
        platform: platform === 'all' ? undefined : platform,
      });
      set({
        notifications,
        unreadCount:
          platform === 'all' ? notifications.filter((n) => !n.is_read).length : get().unreadCount,
        loading: false,
        loaded: true,
      });
    } catch (err) {
      set({ loading: false });
      // Silent — the bell doesn't need to toast on poll failure.
      // eslint-disable-next-line no-console
      console.warn('[notifications] fetch failed', err);
    }
  },

  refreshCount: async () => {
    try {
      const { count } = await notificationService.unreadCount();
      set({ unreadCount: count });
    } catch {
      /* silent */
    }
  },

  markRead: async (id) => {
    // Optimistic update — don't wait for server to drop the unread state.
    set({
      notifications: get().notifications.map((n) =>
        n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
      ),
      unreadCount: Math.max(0, get().unreadCount - 1),
    });
    try {
      await notificationService.markRead(id);
    } catch {
      // If the server rejected, resync on the next poll.
    }
  },

  markAllRead: async () => {
    set({
      notifications: get().notifications.map((n) =>
        n.is_read ? n : { ...n, is_read: true, read_at: new Date().toISOString() }
      ),
      unreadCount: 0,
    });
    try {
      await notificationService.markAllRead();
    } catch {
      /* silent — next poll resyncs */
    }
  },

  setPlatformFilter: (platform) => {
    set((state) => ({
      filters: { ...state.filters, platform },
      loaded: false,
    }));
    get().fetch();
  },

  remove: async (id) => {
    const current = get().notifications;
    const target = current.find((n) => n.id === id);

    set({
      notifications: current.filter((n) => n.id !== id),
      unreadCount: target && !target.is_read ? Math.max(0, get().unreadCount - 1) : get().unreadCount,
    });

    try {
      await notificationService.remove(id);
    } catch {
      // If backend delete fails, recover on the next poll.
      get().fetch();
    }
  },
}));
